// The Image Structure's editor's rows and its save (spec docs/specs/web/library-image-structure.md,
// PR 2), as live's `structure-editor` (`schema.ts`, `utils.ts`): each part's rows, the elements it
// can add, and the `PATCH` that sends every element, null for those taken away or left empty.
import type { AdCreativeStructure, AdStructureUpdate } from "../../api/generated/model";
import {
  TEXT_ELEMENTS,
  VISUAL_ELEMENTS,
  shownElements,
  type StructureElement,
  type StructurePart,
  type TextKey,
  type VisualKey,
} from "./structure";

/** An element in the editor: its key, its text and its placement. */
export interface EditRow<Key extends string> {
  key: Key;
  value: string;
  placement: string;
}

/** The editor's rows, a list a part. */
export interface StructureDraft {
  texts: EditRow<TextKey>[];
  visuals: EditRow<VisualKey>[];
}

function rowsOf<Key extends string>(
  elements: readonly StructureElement<Key>[],
  part: StructurePart<Key> | null | undefined,
): EditRow<Key>[] {
  return shownElements(elements, part).map(({ element, value, placement }) => ({ key: element.key, value, placement }));
}

/** The editor's first rows: the elements the structure holds a text for, in live's order. */
export function draftOf(structure: AdCreativeStructure): StructureDraft {
  return {
    texts: rowsOf(TEXT_ELEMENTS, structure.text_elements),
    visuals: rowsOf(VISUAL_ELEMENTS, structure.visual_style),
  };
}

/** The elements a part does not show, in live's order: its Additional elements. */
export function missing<Key extends string>(
  elements: readonly StructureElement<Key>[],
  rows: readonly EditRow<Key>[],
): StructureElement<Key>[] {
  return elements.filter((element) => !rows.some((row) => row.key === element.key));
}

/** A part as the `PATCH` sends it: every element's text or null, and its placement, null without a text. */
function partOf<Key extends string>(elements: readonly StructureElement<Key>[], rows: readonly EditRow<Key>[]): StructurePart<Key> {
  const part: StructurePart<Key> = {};
  for (const element of elements) {
    const row = rows.find((each) => each.key === element.key);
    const value = row === undefined || row.value.trim() === "" ? null : row.value.trim();
    part[element.key] = value;
    part[element.placement] = value === null || row === undefined || row.placement.trim() === "" ? null : row.placement.trim();
  }
  return part;
}

/** The save: every text and visual, so one taken away is cleared; `ad_categories` is not sent, so it stays. */
export function updateOf(draft: StructureDraft): AdStructureUpdate {
  return { text_elements: partOf(TEXT_ELEMENTS, draft.texts), visual_style: partOf(VISUAL_ELEMENTS, draft.visuals) };
}
